import Modal from "@mui/material/Modal";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../service/Api";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

interface SyncCommentsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type syncingStatusType = "initial" | "syncing" | "synced" | "failed";

export const SyncCommentsModal: React.FC<SyncCommentsModalProps> = ({
  open,
  setOpen,
}) => {
  const { id } = useParams<{ id: string }>();
  const [syncingStatus, setSyncingStatus] =
    useState<syncingStatusType>("initial");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const syncComments = useCallback(async () => {
    setSyncingStatus("syncing");

    try {
      await apiService.get(`/api/streams/stream/${id}/fetch-youtube-comments/`);
      setSyncingStatus("synced");
    } catch (error) {
      const errMsg =
        typeof (error as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail === "string"
          ? (error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail
          : null;
      setErrorMessage(errMsg || "Failed to sync comments");
      setSyncingStatus("failed");
    }
  }, [id]);

  useEffect(() => {
    if (!open) {
      return;
    }
    syncComments();
  }, [open, syncComments]);

  return (
    <Modal
      open={open}
      //onClose={() => setOpen(false)}
      aria-labelledby="syncing-comments-modal"
      aria-describedby="syncing-comments-modal-description"
    >
      <Box
        sx={{
          borderRadius: 1,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 350,
          bgcolor: "background.paper",
          boxShadow: 24,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            padding: "0 .5em",
            borderBottom: `1px solid whitesmoke`,
          }}
        >
          <IconButton sx={{ "&:hover": { color: "#a60b0bff" } }}>
            <CloseIcon onClick={() => setOpen(false)} />
          </IconButton>
        </Box>

        {syncingStatus === "syncing" && (
          <Stack sx={{ padding: "2em 0" }}>
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <CircularProgress size={30} />
            </Box>
            <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
              Syncing comments
            </Typography>
          </Stack>
        )}

        {syncingStatus === "synced" && (
          <Stack sx={{ padding: "2em 0" }}>
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <TaskAltIcon sx={{ width: 50, height: 50, color: "#048b3dff" }} />
            </Box>
            <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
              100 Comments Synced
            </Typography>
          </Stack>
        )}

        {syncingStatus === "failed" && (
          <Stack sx={{ padding: "2em 0" }}>
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <ErrorOutlineIcon
                sx={{ width: 50, height: 50, color: "#a60b0bff" }}
              />
            </Box>
            <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
              {errorMessage}
            </Typography>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

SyncCommentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};
